import {Tool} from '@app/modules/svg/service/tool/Tool';
import {Cursor} from '@app/modules/svg/dataSource/constant/Cursor';
import {Point} from '@app/modules/svg/model/Point';
import {Container} from '@app/modules/svg/Container';
import {SVGEvent} from '@app/modules/svg/dataSource/constant/SVGEvent';
import {document} from 'ngx-bootstrap/utils';
import {Path} from '@app/modules/svg/model/path/Path';
import {PathView} from '@app/modules/svg/element/shape/path/PathView';
import {Color, Fill, ImageProcessor} from '@app/modules/svg/service/tool/fill/ImageProcessor';
import {CoordinatePlaneView2} from "@app/modules/svg/element/complex/cartesian2/CoordinatePlaneView2";
import {ElementView} from "@app/modules/svg/element/ElementView";
import {withLatestFrom} from "rxjs/operators";

export class FillTool extends Tool {
  protected override _cursor: Cursor = Cursor.FILL;

  public fillColor: string = this._container.style.fillColor;

  public constructor(container: Container) {
    super(container);

    this.mouseDownEvent = this.mouseDownEvent.bind(this);
    this.mouseMoveEvent = this.mouseMoveEvent.bind(this);
    this.mouseUpEvent = this.mouseUpEvent.bind(this);
  }

  private mouseDownEvent(event: MouseEvent | TouchEvent): void {
    this._container.HTML.addEventListener('mousemove', this.mouseMoveEvent);
    this._container.HTML.addEventListener('touchmove', this.mouseMoveEvent);
    document.addEventListener('mouseup', this.mouseUpEvent);
    document.addEventListener('touchend', this.mouseUpEvent);

    this.makeMouseDown(Container.eventToPosition(event));
  };
  private mouseMoveEvent(event: MouseEvent | TouchEvent): void {
    this.makeMouseMove(Container.eventToPosition(event));
  };
  private mouseUpEvent(event: MouseEvent | TouchEvent): void {
    this._container.HTML.removeEventListener('mousemove', this.mouseMoveEvent);
    this._container.HTML.removeEventListener('touchmove', this.mouseMoveEvent);
    document.removeEventListener('mouseup', this.mouseUpEvent);
    document.removeEventListener('touchend', this.mouseUpEvent);
    this.makeMouseUp(Container.eventToPosition(event));
  };

  public makeMouseDown(position: Point, call?: boolean, additional?: any): void {
  }
  public makeMouseMove(position: Point, call?: boolean, additional?: any): void {
  }
  public makeMouseUp(position: Point, call?: boolean, additional?: any): void {
    const containerRect: DOMRect = this._container.HTML.getBoundingClientRect();
    const mousePosition = {
      x: Math.floor(position.x - containerRect.left),
      y: Math.floor(position.y - containerRect.top),
    };

    this.renderAsImage().then((image: ImageProcessor): void => {
      const fill: Fill = image.fill(mousePosition, Color.fromHex(this.fillColor));
      if (fill.touchesTheBorder) {
        return;
      }

      const path: Path = ImageProcessor.matrixToFillLines(fill.matrix);

      const pathView = new PathView(this._container, { globalStyle: true, overEvent: true }, path);
      pathView.style.strokeWidth = '1';
      pathView.style.strokeColor = this.fillColor;
      pathView.style.strokeDashArray = '';
      pathView.style.shapeRendering = 'optimizeSpeed';

      this._container.add(pathView);

      this._container.__call__(SVGEvent.ENCLOSED_AREA_FILLED, { position, element: pathView });
    });
  }

  private async renderAsImage(): Promise<ImageProcessor> {
    const canvasElement: HTMLCanvasElement = document.createElement('canvas');
    const image: HTMLImageElement = document.createElement('img');

    const whiteboardRect = this._container.HTML.getBoundingClientRect();

    canvasElement.width = whiteboardRect.width;
    canvasElement.height = whiteboardRect.height;

    const isGridOn: boolean = this._container.grid.isGridOn();
    this._container.grid.gridOff(false);

    const graphsWithGrid: { ownerId: string; index: number }[] = [...this._container.elements]
      .filter((element): boolean =>
        element instanceof CoordinatePlaneView2 && element.grid.show
      )
      .map((element: CoordinatePlaneView2): { ownerId: string; index: number } => {
        element.grid.show = false;
        element.__updateView__();
        return {
          ownerId: element.ownerId,
          index: element.index,
        };
      });

    const whiteboardClone = this._container.HTML.cloneNode(true) as HTMLElement;

    if (isGridOn) {
      this.container.grid.gridOn(false);
    }

    graphsWithGrid.forEach((id): void => {
      const element: ElementView | undefined = this._container.getElementById(id.ownerId, id.index);
      if (!element) {
        return;
      }

      const coordinatePlane = element as CoordinatePlaneView2;

      coordinatePlane.grid.show = true;
      coordinatePlane.__updateView__();
    });

    whiteboardClone.setAttribute('width', whiteboardRect.width + '');
    whiteboardClone.setAttribute('height', whiteboardRect.height + '');
    FillTool.removeChildrenRecursive(whiteboardClone, 'foreignObject');
    FillTool.removeChildrenRecursive(whiteboardClone, 'image');

    whiteboardClone
      .querySelectorAll('[stroke-dasharray]')
      .forEach((node) => {
        node.removeAttribute('stroke-dasharray');
    });

    /* convert to SVG string */
    const whiteboardSVG: string = new XMLSerializer().serializeToString(whiteboardClone);

    /* make it base64 */
    const whiteboardSVGBase64: string = btoa(whiteboardSVG);

    const context: CanvasRenderingContext2D = canvasElement.getContext('2d');

    const renderPromise: Promise<ImageProcessor> = new Promise(resolve => {
      image.onload = (): void => {
        context.drawImage(image, 0, 0);
        const imageData: ImageData = context.getImageData(0, 0, whiteboardRect.width, whiteboardRect.height);

        resolve(new ImageProcessor(imageData));
      };
      image.onerror = (event): void => {
        console.log(`Error while rendering whiteboard as image: ${JSON.stringify(event)}`);
      };
    });

    /* prepend a "header" */
    image.src = 'data:image/svg+xml;base64,' + whiteboardSVGBase64;

    return renderPromise;
  }

  private static removeChildrenRecursive(element: HTMLElement, tagName: string): void {
    if (typeof element.getElementsByTagName === 'undefined') {
      return;
    }

    for (const child of Array.from(element.getElementsByTagName(tagName))) {
      child.remove();
    }

    for (const child of Array.from(element.childNodes)) {
      FillTool.removeChildrenRecursive(child as HTMLElement, tagName);
    }
  }

  public override on(call: boolean = true): boolean {
    if (!super.on(call)) {
      return false;
    }

    this._container.HTML.addEventListener('mousedown', this.mouseDownEvent);
    this._container.HTML.addEventListener('touchstart', this.mouseDownEvent);

    this._container.style.changeCursor(this.cursor);
    if (call) {
      this._container.__call__(SVGEvent.FILL_TOOl_ON);
    }
    return true;
  }
  public override off(call: boolean = true): boolean {
    if (!super.off(call)) {
      return false;
    }

    this._container.HTML.removeEventListener('mousedown', this.mouseDownEvent);
    this._container.HTML.removeEventListener('touchstart', this.mouseDownEvent);

    if (call) {
      this._container.__call__(SVGEvent.FILL_TOOl_OFF);
    }
    return true;
  }
}
